'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { EventForm, type EventFormRef } from '@/components/partials/events'
import { useCreateEvent } from '@/hooks'
import { type CreateEventParams } from '@/types/event'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const eventFormRef = useRef<EventFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createEvent, isPending: isLoading } = useCreateEvent()

  const handleSubmit = (data: CreateEventParams) => {
    createEvent(data, {
      onSuccess: async newEvent => {
        setIsRedirecting(true)
        queryClient.setQueryData(['event', newEvent.id], newEvent)
        await queryClient.invalidateQueries({ queryKey: ['events'] })
        toast.success(t('created_successfully'))
        router.push(`/events/${newEvent.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateEventParams) => {
    createEvent(data, {
      onSuccess: async newEvent => {
        queryClient.setQueryData(['event', newEvent.id], newEvent)
        await queryClient.invalidateQueries({ queryKey: ['events'] })
        const detailUrl = `/events/${newEvent.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        eventFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (eventFormRef.current) {
        eventFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/events')
  }

  return (
    <EventForm
      key={formKey}
      ref={eventFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
